import Footer from '_components/Footer'
import Header from '_components/Header'
import useBodyClass from '_hooks/useBodyClass'
import useOrderDiscount from '_hooks/useOrderDiscount'
import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap'

import { removePayOnLinkShareMode } from '_utils/localData'
import ModalJoinGroup from './components/ModalJoinGroup'
import NotifyPopup from './components/NotifyPopup'
import UserAddressForm from './components/UserAddressForm'
import UserLoginForm from './components/UserLoginForm'
import UserRegisterForm from './components/UserRegisterForm'
import useAddressBook from '../hooks/useAddressBook'
import useEventDetailData from '../hooks/useEventDetailData'
import useOrderSubmission from '../hooks/useOrderSubmission'
import useOrderSummary from '../hooks/useOrderSummary'
import useProductOrder from '../hooks/useProductOrder'
import { formatPrice } from '../utils/productTransforms'

import AddAddressDeliveryModal from '../components/AddAddressDeliveryModal'
import ChangeDeliveryAddress from '../components/ChangeDeliveryAddress'
import EventSummaryCard from './components/EventSummaryCard'
import OrderSummaryFooter from './components/OrderSummaryFooter'
import ProductListSection from './components/ProductListSection'
import PurchaseSuccessModal from './components/PurchaseSuccessModal'
import DiscountInfor from '../ManageOrder/components/DiscountInfor'

import { useShareGroupJoin } from './hooks/useShareGroupJoin'
import { useShareGuestAccount } from './hooks/useShareGuestAccount'
import { useShareInitialParams } from './hooks/useShareInitialParams'
import { useShareLogin } from './hooks/useShareLogin'
import { useShareOrderFlow } from './hooks/useShareOrderFlow'
import { useShareProductSelection } from './hooks/useShareProductSelection'
import { useShareUserInfo } from './hooks/useShareUserInfo'
import './style.scss'

const EventDetailV2Share = ({ history }) => {
  useBodyClass('event-detail-v2')

  const { eid, oid, shareCode, sharedId, isEventShop } = useShareInitialParams()

  useEffect(() => {
    removePayOnLinkShareMode()
  }, [])

  const eventData = useEventDetailData({ eid, oid, shareCode, sharedId })
  const {
    isLoading,
    noEventFound,
    eventDetail,
    groupDetail,
    edittingOrder,
    payLater,
    adminOrHost,
    isAllowManageEvent,
    allowPlaceOrder,
    collectionTimeStart,
    collectionTimeEnd,
    checkShareEventHaveGroupIsPrivate,
    editOrderBookingAddress,
    editOrderDeliveryDate,
    productInit,
    initialComment,
    listImages,
  } = eventData

  const { userInfo, setUserInfo, userInGroup } = useShareUserInfo(groupDetail)

  const isGuestCheckout = !userInfo && sharedId
  const shouldShowAppLayout = !!userInfo

  const [guestFormData, setGuestFormData] = useState(null)

  const addressBook = useAddressBook({
    userId: userInfo?.id,
    edittingOrder,
    editOrderBookingAddress,
    editOrderDeliveryDate,
  })

  const productOrder = useProductOrder(productInit)
  const { calculatePricePerProduct, totalPrice, totalPriceEdit } = useOrderSummary({
    newOrderProducts: productOrder.newOrderProducts,
    newOrderProductsIndividual: productOrder.newOrderProductsIndividual,
    quantities: productOrder.quantities,
    productOrderRender: productOrder.productOrderRender,
    productOrderRenderIndividual: productOrder.productOrderRenderIndividual,
    findProductById: productOrder.findProductById,
  })

  const itemsTotal = edittingOrder ? totalPriceEdit : totalPrice

  const { discountAmount, originalTotal, finalTotal } = useOrderDiscount({
    orderDiscountEnabled: eventDetail?.orderDiscountEnabled,
    orderDiscount: eventDetail?.orderDiscount,
    itemsTotal,
    adminCost: eventDetail?.adminCost,
    newOrderProducts: productOrder?.newOrderProducts || [],
    quantities: productOrder?.quantities || {},
    newOrderProductsIndividual: productOrder?.newOrderProductsIndividual || [],
    edittingOrder,
  })

  const { hasSelectedProductsToPurchase } = useShareProductSelection({
    newOrderProducts: productOrder.newOrderProducts,
    quantities: productOrder.quantities,
  })

  const submission = useOrderSubmission({
    eid,
    oid,
    shareCode,
    sharedId,
    isGuestCheckout,
    guestFormData,
    isEventShop,
    edittingOrder,
    eventDetail,
    editOrderBookingAddress,
    payLater,
    initialComment,
    initTotalOrderCount: productOrder.initTotalOrderCount,
    newOrderProducts: productOrder.newOrderProducts,
    newOrderProductsIndividual: productOrder.newOrderProductsIndividual,
    quantities: productOrder.quantities,
    addressBook,
    history,
  })

  const guestAccount = useShareGuestAccount({
    guestFormData,
    eventDetail,
    addressBook,
    submission,
    setUserInfo,
  })

  const loginFlow = useShareLogin({
    guestFormData,
    submission,
    setUserInfo,
    closeNotifyPopup: guestAccount.closeNotifyPopup,
  })

  const groupJoin = useShareGroupJoin({ groupDetail })

  const orderFlow = useShareOrderFlow({
    isGuestCheckout,
    guestFormData,
    eventDetail,
    addressBook,
    submission,
    hasSelectedProductsToPurchase,
    checkExistingGuestUser: guestAccount.checkExistingGuestUser,
    openRegisterModal: guestAccount.openRegisterModal,
    userInGroup,
    hasRequestedJoin: groupJoin.hasRequestedJoin,
    setShowModalJoin: groupJoin.setShowModalJoin,
  })

  if (checkShareEventHaveGroupIsPrivate) {
    return <p id="edv2-alert-private-group">Viewing permitted for members only!!</p>
  }

  if (isLoading) {
    return <div className="edv2-page">Loading...</div>
  }

  if (noEventFound) {
    return <div className="edv2-page">No Event Found</div>
  }

  return (
    <div className='main-mobile-layout'>
      {shouldShowAppLayout && <Header />}
      <section
        className={`section-content ${shouldShowAppLayout ? 'section-content--top mains-screen' : 'section-content--top'}`}
        style={{ paddingTop: shouldShowAppLayout ? undefined : '0px' }}
      >
        <div className="container">
          <div className="edv2-page">
            <PurchaseSuccessModal
              show={submission.showModal}
              orderNo={submission.orderNo}
              hasItemWithAckRequired={submission.hasItemWithAckRequired}
              to={`/event/${(userInfo?.id || guestFormData?.createAccount) ? 'purchase-detail' : 'purchase-detail-share'}?ref=${submission.orderNo}`}
            />

            <EventSummaryCard
              eventDetail={eventDetail}
              groupDetail={groupDetail}
              listImages={listImages}
              collectionTimeStart={collectionTimeStart}
              collectionTimeEnd={collectionTimeEnd}
              isAllowManageEvent={isAllowManageEvent}
            />

            <DiscountInfor
              isDiscountEnabled={eventDetail?.orderDiscountEnabled}
              orderDiscount={eventDetail?.orderDiscount}
              showRequestButton={false}
            />

            <ProductListSection
              defaultItemView={eventDetail?.defaultItemView}
              edittingOrder={edittingOrder}
              products={eventDetail.products}
              productOrderRender={productOrder.productOrderRender}
              productOrderRenderIndividual={productOrder.productOrderRenderIndividual}
              newOrderProductsIndividual={productOrder.newOrderProductsIndividual}
              quantities={productOrder.quantities}
              setQuantities={productOrder.setQuantities}
              findProductById={productOrder.findProductById}
              calculatePricePerProduct={calculatePricePerProduct}
              checkMoreThanLimitPerOrder={productOrder.checkMoreThanLimitPerOrder}
              getValueSelect={productOrder.getValueSelect}
              handleChangeSubItem={productOrder.handleChangeSubItem}
              changeProductQuantity={productOrder.changeProductQuantity}
              changeProductQuantityIndividual={productOrder.changeProductQuantityIndividual}
              changeProductQuantityInput={productOrder.changeProductQuantityInput}
              addAnotherOrder={productOrder.addAnotherOrder}
              deleteItemOrder={productOrder.deleteItemOrder}
            />

            <OrderSummaryFooter
              payLater={payLater}
              totalPrice={formatPrice(finalTotal)}
              discountAmount={discountAmount}
              originalTotal={formatPrice(originalTotal)}
              adminCost={formatPrice(eventDetail.adminCost)}
              termCond={eventDetail?.termCond}
              showPurchaseOnBehalf={!oid && !shareCode && adminOrHost}
              isPurchase={submission.isPurchase}
              onTogglePurchase={() => submission.setIsPurchase((prev) => !prev)}
              phoneOrEmail={submission.phoneOrEmail}
              onChangePhoneOrEmail={submission.setPhoneOrEmail}
              bookingAddressRequired={eventDetail.bookingAddressRequired}
              edittingOrder={edittingOrder}
              displayAddress={addressBook.displayAddress}
              displayDeliveryDate={addressBook.displayDeliveryDate}
              onChangeAddressClick={addressBook.toggleChangeAddressModal}
              comment={submission.comment}
              onChangeComment={submission.setComment}
              error={submission.error}
              allowPlaceOrder={allowPlaceOrder}
              isSubmitted={submission.isSubmitted}
              onCancel={() => history.goBack()}
              onSubmit={orderFlow.handleSubmitOrder}
              userInfo={userInfo}
              isGuestCheckout={isGuestCheckout}
              onGuestFormChange={setGuestFormData}
              onLoginClick={loginFlow.openLoginModal}
            />

            {userInfo?.phone && (
              <AddAddressDeliveryModal
                isOpen={addressBook.isAddressModalOpen}
                toggle={addressBook.toggleAddressModal}
                onAddressAdded={submission.handleAddressAdded}
                phoneNumber={userInfo?.phone || ''}
                userAddresses={addressBook.userAddresses}
                eventDetail={eventDetail}
              />
            )}

            <ChangeDeliveryAddress
              isOpen={addressBook.isChangeAddressModalOpen}
              toggle={addressBook.toggleChangeAddressModal}
              userAddresses={addressBook.userAddresses}
              selectedAddress={addressBook.selectedAddressForModal}
              onAddressSelected={submission.handleAddressSelectedAndProceed}
              onAddNewAddress={addressBook.handleOpenAddNewAddress}
              onAddressUpdated={addressBook.refreshAddresses}
              onAddressAdded={submission.handleAddressAddedAndProceed}
              eventDetail={eventDetail}
            />

            <ModalJoinGroup
              modal={groupJoin.showModalJoin}
              toggle={() => groupJoin.setShowModalJoin(!groupJoin.showModalJoin)}
              groupInfo={groupDetail?.group}
              passCodeValue={groupJoin.passCodeValue}
              setPassCodeValue={groupJoin.setPassCodeValue}
              questionValue={groupJoin.questionValue}
              setQuestionValue={groupJoin.setQuestionValue}
              onSubmitJoin={() => groupJoin.onSubmitJoin(orderFlow.proceedOrderFlow)}
              onSkip={() => groupJoin.handleSkipJoin(orderFlow.proceedOrderFlow)}
            />

            <Modal
              isOpen={orderFlow.isGuestAddressModalOpen}
              toggle={orderFlow.closeGuestAddressModal}
              size="lg"
              centered
              style={{ maxWidth: '498px' }}
            >
              <ModalHeader toggle={orderFlow.closeGuestAddressModal}>Delivery Details</ModalHeader>
              <ModalBody>
                <UserAddressForm
                  ref={orderFlow.guestAddressFormRef}
                  onFormChange={orderFlow.setGuestAddressData}
                  showIsDefault={!!guestFormData?.createAccount}
                />
              </ModalBody>
              <ModalFooter>
                <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
                  <Button color="light" onClick={orderFlow.closeGuestAddressModal} style={{ border: '1px solid #07C3C3' }}>
                    Cancel
                  </Button>
                  <Button color="primary" onClick={orderFlow.handleGuestAddressSubmit} style={{ borderRadius: '25px' }} disabled={submission.isSubmitted}>
                    {submission.isSubmitted ? <Spinner size="sm" /> : 'Submit'}
                  </Button>
                </div>
              </ModalFooter>
            </Modal>

            {guestAccount.isRegisterModalOpen && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onClick={guestAccount.closeRegisterModal}
              >
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{ maxWidth: '24rem', pointerEvents: 'none' }}
                >
                  <div
                    style={{ width: '100%', pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UserRegisterForm
                      onClose={guestAccount.closeRegisterModal}
                      onSubmitPIN={guestAccount.handleRegisterPIN}
                      onGoogleSuccess={guestAccount.handleRegisterGoogle}
                      isSubmitting={guestAccount.isRegisterSubmitting}
                    />
                  </div>
                </div>
              </div>
            )}

            <NotifyPopup
              isOpen={guestAccount.isNotifyPopupOpen}
              onClose={guestAccount.closeNotifyPopup}
              onLoginClick={loginFlow.handleOpenLoginFromNotify}
            />

            {loginFlow.isLoginModalOpen && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onClick={loginFlow.closeLoginModal}
              >
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{ maxWidth: '24rem', pointerEvents: 'none' }}
                >
                  <div
                    style={{ width: '100%', pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UserLoginForm
                      onClose={loginFlow.closeLoginModal}
                      onSubmitLogin={loginFlow.handleLogin}
                      onGoogleSuccess={loginFlow.handleLoginGoogle}
                      isSubmitting={loginFlow.isLoginSubmitting}
                      initialEmail={guestFormData?.email || ''}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {shouldShowAppLayout && <Footer />}
    </div>
  )
}

export default withRouter(EventDetailV2Share)
